import { useEffect, useState } from "react";
import { API } from "@/App";
import { toast } from "sonner";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { GitMerge, Trash2, Copy, X, Check, Clock, UserCheck } from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const post = async (url, body) => {
  const res = await fetch(url, { 
    method: "POST", 
    credentials: "include", 
    headers: { "Content-Type": "application/json" }, 
    body: JSON.stringify(body || {}) 
  });
  const d = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(d.detail || "İşlem başarısız");
  return d;
};

const DuplicateRow = ({ m, byId, onChanged, onRequestConfirm }) => {
  const act = async (fn, okMsg) => { 
    try { 
      const r = await fn(); 
      toast.success(r.message || okMsg); 

      // SOL MENÜDEKİ ROZETİ ANINDA GÜNCELLE:
      window.dispatchEvent(new Event("memberBadgesUpdated"));
      
      if (onChanged) onChanged(); 
    } catch (e) { 
      toast.error(e.message); 
    } 
  };

  const matches = (m.possible_duplicate_of || []).map((id) => byId[id]).filter(Boolean);

  return (
    <div className="rounded-xl border border-amber-200 bg-white p-3 space-y-2" data-testid={`duplicate-row-${m.member_id}`}>
      <div className="flex flex-wrap items-center gap-2">
        <Copy className="w-4 h-4 text-amber-600" />
        <span className="font-medium text-navy">{m.name} {m.surname}</span>
        <span className="text-xs text-grey">{m.phone} · {m.member_no || "No yok"} · linkten {(m.created_at || "").slice(0, 10).split("-").reverse().join(".")}</span>
        <div className="flex-1" />
        
        <Button size="sm" variant="outline" className="rounded-lg h-8" 
          onClick={() => act(() => post(`${API}/members/${m.member_id}/link-approve`), "Yoksayıldı")} 
          data-testid={`duplicate-dismiss-${m.member_id}`}>
          <X className="w-4 h-4 mr-1" />Farklı kişi (Yoksay)
        </Button>

        <Button size="sm" variant="outline" className="rounded-lg h-8 text-red-600 border-red-200 hover:bg-red-50 hover:text-red-700" 
          onClick={() => onRequestConfirm({
            title: "Kaydı Silmek İstiyor Musunuz?",
            description: `${m.name} ${m.surname} üyesi ve oluşturulan geçici rezervasyonları kalıcı olarak silinecektir.`,
            confirmText: "Evet, Sil",
            isDanger: true,
            onConfirm: () => act(() => post(`${API}/members/${m.member_id}/link-discard`), "Silindi")
          })} 
          data-testid={`duplicate-discard-${m.member_id}`}>
          <Trash2 className="w-4 h-4 mr-1" />Sil
        </Button>
      </div>

      <div className="rounded-lg bg-slate-50 p-2 space-y-1">
        <p className="text-xs text-grey">Aynı isimli mevcut üye:</p>
        {matches.map((c) => (
          <div key={c.member_id} className="flex flex-wrap items-center gap-2 text-sm">
            <span className="font-medium text-navy">{c.name} {c.surname}</span>
            <span className="text-xs text-grey">{c.phone || "telefon yok"} · {c.member_no || ""}</span>
            <div className="flex-1" />
            
            <Button size="sm" className="rounded-lg h-7 bg-emerald-600 hover:bg-emerald-700 text-white" 
              data-testid={`duplicate-merge-${m.member_id}-${c.member_id}`}
              onClick={() => onRequestConfirm({
                title: "Kayıtları Birleştir",
                description: `Linkten gelen kayıt, ${c.name} ${c.surname} (${c.member_no || "No yok"}) ile birleştirilecektir. Tüm rezervasyonlar bu üyeye aktarılır.`,
                confirmText: "Evet, Birleştir",
                isDanger: false,
                onConfirm: () => act(() => post(`${API}/members/${m.member_id}/link-merge`, { target_member_id: c.member_id }), "Birleştirildi")
              })}>
              <GitMerge className="w-3.5 h-3.5 mr-1" />Aynı kişi, birleştir
            </Button>
          </div>
        ))}
        {!matches.length && <p className="text-xs text-grey">Eşleşen üye artık yok.</p>}
      </div>
    </div>
  );
};

export const LinkPendingPanel = ({ businessId, onChanged, refreshKey }) => {
  const [all, setAll] = useState([]);
  const [requests, setRequests] = useState([]);
  const [confirmConfig, setConfirmConfig] = useState(null);
  const [processingId, setProcessingId] = useState(null); // Çift tıklama koruması

  useEffect(() => {
    if (!businessId) return;
    
    // Üyeleri çek
    fetch(`${API}/businesses/${businessId}/members`, { credentials: "include" })
      .then((r) => (r.ok ? r.json() : []))
      .then(setAll)
      .catch(() => setAll([]));

    // Onay bekleyen ders/üye taleplerini çek
    fetch(`${API}/businesses/${businessId}/booking-requests`, { credentials: "include" })
      .then((r) => (r.ok ? r.json() : []))
      .then(setRequests)
      .catch(() => setRequests([]));
  }, [businessId, refreshKey]);

  const actReq = async (url, okMsg, reqId) => {
    if (processingId) return; // Çift tıklamayı engeller
    try {
      setProcessingId(reqId);
      const r = await post(url);
      toast.success(r.message || okMsg);

      // SOL MENÜDEKİ ROZETİ ANINDA GÜNCELLE:
      window.dispatchEvent(new Event("memberBadgesUpdated"));

      if (onChanged) onChanged();
      
      // Talepleri yeniden yükle
      fetch(`${API}/businesses/${businessId}/booking-requests`, { credentials: "include" })
        .then((r) => (r.ok ? r.json() : []))
        .then(setRequests);
    } catch (e) {
      toast.error(e.message);
    } finally {
      setProcessingId(null);
    }
  };

  const byId = Object.fromEntries(all.map((x) => [x.member_id, x]));
  const dups = all.filter((x) => x.possible_duplicate_of?.length);

  if (!dups.length && !requests.length) return null;

  return (
    <>
      <Card className="border-0 shadow-sm bg-amber-50/60" data-testid="link-pending-panel">
        <CardContent className="p-4 space-y-4">
          {/* 1. YENİ GELEN DERS & ÜYELİK TALEPLERİ */}
          {requests.length > 0 && (
            <div className="space-y-2">
              <p className="text-sm font-semibold text-amber-900 flex items-center gap-1.5">
                <UserCheck className="w-4 h-4 text-amber-700" /> Onay Bekleyen Yeni Kayıt Talepleri ({requests.length})
              </p>
              <p className="text-xs text-amber-800">
                Linkten ilk kez katılan bu kişilerin üye kaydı ve rezervasyonu henüz yapılmadı. Onayladığınızda üye kaydı ve seçtiği derse rezervasyon otomatik oluşacaktır.
              </p>
              
              <div className="space-y-2 pt-1">
                {requests.map((rq) => {
                  // Ders dolu mu kontrolünü her iki kanaldan da garantiye alıyoruz:
                  const isClassFull = Boolean(rq.is_full || rq.class_info?.is_full);

                  return (
                    <div key={rq.request_id} className="rounded-xl border border-amber-200 bg-white p-3 space-y-2">
                      <div className="flex flex-wrap items-center gap-2">
                        <Clock className="w-4 h-4 text-amber-600" />
                        <span className="font-bold text-navy">{rq.name} {rq.surname}</span>
                        <span className="text-xs text-grey">{rq.phone} {rq.email ? `· ${rq.email}` : ""}</span>
                        
                        {/* Ders Dolu ise Çıkacak Rozet */}
                        {isClassFull && (
                          <span className="text-[10px] bg-orange-100 text-orange-800 font-semibold px-2 py-0.5 rounded-full border border-orange-200">
                            Ders Dolu (Bekleme Talebi)
                          </span>
                        )}

                        <div className="flex-1" />
                        
                        {/* Akıllı Onayla / Bekleme Listesine Al Butonu */}
                        <Button 
                          size="sm" 
                          disabled={processingId === rq.request_id}
                          className={`rounded-lg h-8 text-white disabled:opacity-50 ${
                            isClassFull ? "bg-amber-600 hover:bg-amber-700" : "bg-emerald-600 hover:bg-emerald-700"
                          }`}
                          onClick={() => setConfirmConfig({
                            title: isClassFull ? "Kaydet ve Bekleme Listesine Al" : "Kayıt Talebini Onayla",
                            description: isClassFull 
                              ? `${rq.name} ${rq.surname} sisteme yeni üye olarak kaydedilecek ve ders kontenjanı dolu olduğu için bekleme listesine alınacaktır. Onaylıyor musunuz?`
                              : `${rq.name} ${rq.surname} için üye kaydı oluşturulup seçtiği derse rezervasyonu eklenecektir. Onaylıyor musunuz?`,
                            confirmText: isClassFull ? "Evet, Kaydet ve Bekleme Listesine Al" : "Evet, Onayla ve Kaydet",
                            isDanger: false,
                            onConfirm: () => actReq(
                              `${API}/booking-requests/${rq.request_id}/approve`, 
                              isClassFull ? "Üye kaydedildi ve bekleme listesine alındı" : "Talep onaylandı, üye ve rezervasyon oluşturuldu", 
                              rq.request_id
                            )
                          })}
                        >
                          <Check className="w-4 h-4 mr-1" />
                          {processingId === rq.request_id 
                            ? "İşleniyor..." 
                            : (isClassFull ? "Kaydet ve Bekleme Listesine Al" : "Onayla ve Kaydet")}
                        </Button>

                        {/* Reddet Butonu */}
                        <Button 
                          size="sm" 
                          variant="outline" 
                          disabled={processingId === rq.request_id}
                          className="rounded-lg h-8 text-red-600 border-red-200 hover:bg-red-50 disabled:opacity-50"
                          onClick={() => setConfirmConfig({
                            title: "Talebi Reddet",
                            description: `${rq.name} ${rq.surname} kişisinin ders kayıt talebi reddedilecektir. Üye veya rezervasyon oluşturulmayacaktır.`,
                            confirmText: "Evet, Reddet",
                            isDanger: true,
                            onConfirm: () => actReq(`${API}/booking-requests/${rq.request_id}/reject`, "Talep reddedildi", rq.request_id)
                          })}
                        >
                          <X className="w-4 h-4 mr-1" />
                          Reddet
                        </Button>
                      </div>

                      {/* Talep Edilen Ders Bilgileri */}
                      {rq.class_info && (
                        <div className="rounded-lg bg-amber-50/50 p-2 text-xs text-amber-900 flex flex-wrap items-center gap-x-3 gap-y-1">
                          <span><b>Talep Edilen Ders:</b> {rq.class_info.branch ? `${rq.class_info.branch} · ` : ""}{rq.class_info.class_type}</span>
                          <span><b>Tarih:</b> {rq.class_info.date} ({rq.class_info.start_time} - {rq.class_info.end_time})</span>
                          {rq.class_info.trainer_name && <span><b>Antrenör:</b> {rq.class_info.trainer_name}</span>}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* 2. OLASI TEKRAR KAYITLAR */}
          {dups.length > 0 && (
            <div className="space-y-2 pt-2 border-t border-amber-200/60">
              <p className="text-sm font-semibold text-amber-900">Olası Tekrar Kayıtlar ({dups.length})</p>
              <p className="text-xs text-amber-800">
                Linkten gelen bu kişilerle aynı ad-soyadda başka bir üyeniz var. Aynı kişiyse birleştirin, farklı kişiyse yoksayın.
              </p>
              {dups.map((m) => (
                <DuplicateRow key={m.member_id} m={m} byId={byId} onChanged={onChanged} onRequestConfirm={(config) => setConfirmConfig(config)} />
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Onay Modalı */}
      <AlertDialog open={!!confirmConfig} onOpenChange={(open) => !open && setConfirmConfig(null)}>
        <AlertDialogContent className="rounded-2xl max-w-md">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-lg font-semibold">{confirmConfig?.title}</AlertDialogTitle>
            <AlertDialogDescription className="text-sm text-slate-600">
              {confirmConfig?.description}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="gap-2 sm:gap-0">
            <AlertDialogCancel className="rounded-xl">Vazgeç</AlertDialogCancel>
            <AlertDialogAction
              className={`rounded-xl text-white ${
                confirmConfig?.isDanger 
                  ? "bg-red-600 hover:bg-red-700" 
                  : "bg-emerald-600 hover:bg-emerald-700"
              }`}
              onClick={() => {
                if (confirmConfig?.onConfirm) confirmConfig.onConfirm();
                setConfirmConfig(null);
              }}
            >
              {confirmConfig?.confirmText}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};